import {animate} from './animate';

const INTERACTIVE_ELEMENT_ATTRIBUTE = 'data-interactive-element';

const getContainerWidth = ( container ) => {
	const computedStyle = window.getComputedStyle( container );
	const allWidth = container.offsetWidth;
	const paddingLeft = parseInt( computedStyle.paddingLeft, 10 );
	const paddingRight = parseInt( computedStyle.paddingRight, 10 );
	
	return allWidth - paddingLeft - paddingRight;
};

const getCurrentOffset = ( movingLine ) => {
	return parseInt( window.getComputedStyle( movingLine ).left, 10 );
};

const setOffset = ( newOffset, movingLine ) => {
	movingLine.style.left = `${newOffset}px`;
};

const calculateOffset = ( evtOffset, currentOffset, maxOffset ) => {
	let newOffset = currentOffset + evtOffset;
	
	if ( newOffset > 0 )
	{
		newOffset = 0;
	}
	else if ( newOffset < maxOffset )
	{
		newOffset = maxOffset;
	}
	
	return newOffset;
};

const getScrollParams = ( container, movingLine ) => {
	const containerWidth = getContainerWidth( container );
	const movingLineWidth = movingLine.scrollWidth;
	let maxOffset = containerWidth - movingLineWidth;
	const isScrolling = movingLineWidth > containerWidth;
	
	if ( !isScrolling )
	{
		maxOffset = 0;
	}
	
	return {
		containerWidth,
		movingLineWidth,
		maxOffset,
		isScrolling,
	};
};

const canselInteractivity = ( clickEvt, newOffset ) => {
	const interactiveElement = clickEvt.target.closest( `[${INTERACTIVE_ELEMENT_ATTRIBUTE}]` );
	
	if ( interactiveElement && Math.abs( newOffset ) > 1 )
	{
		clickEvt.preventDefault();
		clickEvt.stopPropagation();
		clickEvt.stopImmediatePropagation();
	}
};

const initMovingLine = ( container, movingLine, options = {} ) => {
	const {
		animDuration = 400,
	} = options;
	let isInteractionStart = false;
	
	const updateScroll = () => {
		const {maxOffset} = getScrollParams( container, movingLine );
		const currentOffset = getCurrentOffset( movingLine );
		
		if ( currentOffset < maxOffset )
		{
			setOffset( maxOffset, movingLine );
		}
	};
	
	movingLine.addEventListener(
		'mousedown',
		( downEvt ) => {
			isInteractionStart = true;
			
			const {maxOffset, isScrolling} = getScrollParams( container, movingLine );
			
			if ( isScrolling )
			{
				const startX = downEvt.x;
				const currentOffset = getCurrentOffset( movingLine );
				
				const handleMoveEvt = ( moveEvt ) => {
					const endX = moveEvt.x;
					const evtOffset = endX - startX;
					const newOffset = calculateOffset( evtOffset, currentOffset, maxOffset );
					
					setOffset( newOffset, movingLine );
				};
				
				const handleUpEvt = () => {
					isInteractionStart = false;
					
					document.removeEventListener( 'mousemove', handleMoveEvt );
					document.removeEventListener( 'mouseup', handleUpEvt );
				};
				
				const handleClickEvt = ( clickEvt ) => {
					canselInteractivity( clickEvt, startX - clickEvt.x );
					
					movingLine.removeEventListener( 'click', handleClickEvt, true );
				};
				
				document.addEventListener( 'mousemove', handleMoveEvt );
				document.addEventListener( 'mouseup', handleUpEvt );
				
				movingLine.addEventListener( 'click', handleClickEvt, true );
			}
		},
	);
	
	const initTouchEvents = () => {
		let startX = 0;
		let currentOffset = getCurrentOffset( movingLine );
		let newOffset = 0;
		let maxOffset = 0;
		let isTouchStart = false;
		let isTouchHorisontalMove = false;
		let isFirstMoveEvt = true;
		
		const handleStartEvt = ( startEvt ) => {
			const props = getScrollParams( container, movingLine );
			const touchEvt = startEvt.touches[0];
			maxOffset = props.maxOffset;
			
			if ( touchEvt && props.isScrolling )
			{
				isInteractionStart = true;
				isTouchStart = true;
				startX = touchEvt.clientX;
				currentOffset = getCurrentOffset( movingLine );
				newOffset = 0;
			}
		};
		
		const handleMoveEvt = ( moveEvt ) => {
			const touchMoveEvt = moveEvt.touches[0];
			
			if ( touchMoveEvt && isTouchStart )
			{
				const endX = touchMoveEvt.clientX;
				const evtOffset = endX - startX;
				
				if ( isFirstMoveEvt && Math.abs( evtOffset ) > 3 )
				{
					isTouchHorisontalMove = true;
				}
				
				if ( isTouchHorisontalMove )
				{
					newOffset = calculateOffset( evtOffset, currentOffset, maxOffset );
					
					setOffset( newOffset, movingLine );
				}
				
				if ( moveEvt.cancelable && isTouchHorisontalMove )
				{
					moveEvt.preventDefault();
				}
				
				if ( isFirstMoveEvt )
				{
					isFirstMoveEvt = false;
				}
			}
		};
		
		const handleEndEvt = () => {
			isTouchStart = false;
			isInteractionStart = false;
			isTouchHorisontalMove = false;
			isFirstMoveEvt = true;
		};
		
		movingLine.addEventListener( 'touchstart', handleStartEvt, {passive: true} );
		document.addEventListener( 'touchmove', handleMoveEvt, {passive: false} );
		document.addEventListener( 'touchend', handleEndEvt, {passive: false} );
	};
	
	initTouchEvents();
	
	movingLine.addEventListener( 'click', ( clickEvt ) => {
		const interactiveElement = clickEvt.target.closest( `[${INTERACTIVE_ELEMENT_ATTRIBUTE}]` );
		
		if ( interactiveElement )
		{
			const {
				containerWidth,
				maxOffset,
				isScrolling,
			} = getScrollParams( container, movingLine );
			const currentOffset = getCurrentOffset( movingLine );
			
			if ( isScrolling )
			{
				const absCurrentOffset = Math.abs( currentOffset );
				const left = interactiveElement.offsetLeft;
				const right = left + interactiveElement.offsetWidth;
				let newOffset = null;
				
				if ( right > ( containerWidth + absCurrentOffset ) )
				{
					newOffset = containerWidth - right;
				}
				
				if ( left < absCurrentOffset || ( newOffset && left < Math.abs( newOffset ) ) )
				{
					newOffset = -1 * left;
				}
				
				if ( newOffset !== null )
				{
					if ( newOffset < maxOffset )
					{
						newOffset = maxOffset;
					}
					
					const diff = newOffset - currentOffset;
					
					let isCancelled = false;
					
					animate( {
						duration: animDuration,
						timing( timeFraction )
						{
							return timeFraction;
						},
						draw( progress )
						{
							if ( !isCancelled )
							{
								setOffset( currentOffset + ( diff * progress ), movingLine );
							}
						},
						cancelCallback()
						{
							if ( isInteractionStart )
							{
								isCancelled = true;
							
								return true;
							}
							
							return false;
						},
					} );
				}
			}
		}
	} );
	
	window.addEventListener( 'resize', updateScroll );
};

export {
	initMovingLine,
};
